import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { download } from '../lib/download'
import { useLayer } from './layers'
import styles from './TaskCentre.module.css'

/* The task centre (handoff §6d, §10.4). Jobs measured in tens of seconds do
   not own the screen: a screen starts a task, the task lands in the rail, and
   the user carries on working. Anything that produces a file goes through
   here, never a direct click-to-download (§6d); the Download button on a
   finished task is the one place a file is handed to the browser.

   Both ends of a job say where it went, and that is handled once, here, not
   per caller: starting one raises a toast naming the task centre by position,
   and finishing raises exactly one more. A chip flies from the button that was
   pressed into the task-centre button, which flashes as it lands; under
   prefers-reduced-motion the toast carries it alone.

   The centre stays lifted above any open modal until it has been opened
   (opening is the acknowledgement, and marks every task seen), and when lifted
   it carries its own opaque surface: surface and z-order switch on the same
   condition.

   Tasks live for this session of the page (TASKS-1): a reload forgets them.
   What a task produced is not lost with it where the server keeps it (a
   payroll export is in the export log). */

export interface TaskFile {
  name: string
  content: BlobPart
  type: string
}

// What a finished job hands back. `problem` is a job that finished but not
// cleanly (a recalculation with a failed target): it is shown on the task and
// the finish toast says to look.
export interface TaskOutcome {
  file?: TaskFile
  note?: string
  problem?: string
}

export interface StartTask {
  label: string
  // The scope line under the task: what it covers.
  note: string
  // The filename, when the browser knows it before the job runs. Its presence
  // says the job produces a file ("Building it"); the chip carries it.
  file?: string
  // Says the job produces a file whose name only the server knows.
  producesFile?: boolean
  // Reports real progress (0-100) when the job can measure it; a job that
  // cannot shows an indeterminate bar, never an invented percentage.
  run: (progress: (pct: number) => void) => Promise<TaskOutcome | void>
  onDone?: (outcome: TaskOutcome) => void
}

interface Task {
  id: string
  label: string
  note: string
  file: TaskFile | null
  pct: number | null
  state: 'running' | 'done' | 'failed'
  problem: string | null
  startedAt: Date
  seen: boolean
}

interface TaskApi {
  startTask: (t: StartTask) => void
}

const KEEP = 8
const TOAST_MS = 3600
const FLIGHT_MS = 820

export const TOAST = {
  building: 'Building it — it will appear under Background tasks, bottom left',
  running: 'Running in the background — follow it under Background tasks, bottom left',
  ready: (label: string) => `${label} ready — download it from Background tasks, bottom left`,
  finished: (label: string) => `${label} finished`,
  problem: (label: string) => `${label} finished with problems — see Background tasks, bottom left`,
  failed: (label: string) => `${label} failed — see Background tasks, bottom left`,
}

// The common case: a file built in the browser from what the screen shows.
// It is built when the task runs, from the rows captured when it started.
export function fileTask(label: string, note: string, name: string, type: string, build: () => BlobPart): StartTask {
  return { label, note, file: name, run: async () => ({ file: { name, content: build(), type } }) }
}

const errorText = (e: any) => {
  const b = e?.body
  return (b && (b.message || b.error)) || e?.message || 'Something went wrong'
}

// Outside the shell (a screen rendered on its own, as in a test) there is no
// rail for a task to land in, so the job runs and hands its file over as soon
// as it is ready. Inside the app the Shell always provides the centre.
const direct: TaskApi = {
  startTask: ({ run, onDone }) => {
    Promise.resolve()
      .then(() => run(() => {}))
      .then((o) => {
        const out = o || {}
        if (out.file) download(out.file.name, out.file.content, out.file.type)
        onDone?.(out)
      })
      .catch((e) => console.error('Task failed:', e))
  },
}

const Ctx = createContext<(TaskApi & { state: CentreState }) | null>(null)

interface CentreState {
  tasks: Task[]
  open: boolean
  landing: boolean
  toast: string
  toggle: () => void
  close: () => void
}

export function useTasks(): TaskApi {
  return useContext(Ctx) || direct
}

const reducedMotion = () => {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

export function TaskCentreProvider({ children }: { children: React.ReactNode }) {
  const [tasks, setTasks] = useState<Task[]>([])
  const [open, setOpen] = useState(false)
  const [landing, setLanding] = useState(false)
  const [toast, setToast] = useState('')
  const seq = useRef(0)
  const clickAt = useRef<{ x: number; y: number } | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const landTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    // Where the flight starts: the last place anything was pressed.
    const onDown = (e: PointerEvent | MouseEvent) => {
      clickAt.current = { x: e.clientX, y: e.clientY }
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('mousedown', onDown, true)
    return () => {
      alive.current = false
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('mousedown', onDown, true)
      if (toastTimer.current) clearTimeout(toastTimer.current)
      if (landTimer.current) clearTimeout(landTimer.current)
    }
  }, [])

  const say = useCallback((msg: string) => {
    if (!alive.current) return
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => alive.current && setToast(''), TOAST_MS)
  }, [])

  const land = useCallback(() => {
    if (!alive.current) return
    setLanding(true)
    if (landTimer.current) clearTimeout(landTimer.current)
    landTimer.current = setTimeout(() => alive.current && setLanding(false), 900)
  }, [])

  // One arc from the pressed button into the task-centre button, no bounce.
  const fly = useCallback(
    (text: string) => {
      try {
        if (reducedMotion()) return
        const from = clickAt.current
        const target = document.querySelector('[data-task-btn]')
        if (!from || !target) return
        const r = target.getBoundingClientRect()
        const to = { x: r.left + r.width / 2, y: r.top + r.height / 2 }
        const chip = document.createElement('div')
        chip.textContent = text
        chip.className = styles.chip
        chip.setAttribute('aria-hidden', 'true')
        chip.style.left = from.x + 'px'
        chip.style.top = from.y + 'px'
        if (typeof chip.animate !== 'function') return
        document.body.appendChild(chip)
        const dx = to.x - from.x
        const dy = to.y - from.y
        const a = chip.animate(
          [
            { transform: 'translate(-50%,-50%) scale(1)', opacity: 1, offset: 0 },
            { transform: `translate(calc(-50% + ${dx * 0.45}px),calc(-50% + ${dy * 0.3 - 60}px)) scale(.86)`, opacity: 1, offset: 0.5 },
            { transform: `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) scale(.3)`, opacity: 0, offset: 1 },
          ],
          { duration: FLIGHT_MS, easing: 'cubic-bezier(.55,.06,.3,1)' }
        )
        a.onfinish = () => {
          chip.remove()
          land()
        }
      } catch {
        /* motion is a cue, not a label: the toast still says where it went */
      }
    },
    [land]
  )

  const patch = (id: string, p: Partial<Task>) => alive.current && setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, ...p } : t)))

  const startTask = useCallback(
    (spec: StartTask) => {
      const id = 't' + ++seq.current
      const makesFile = !!spec.file || !!spec.producesFile
      const task: Task = { id, label: spec.label, note: spec.note, file: null, pct: null, state: 'running', problem: null, startedAt: new Date(), seen: false }
      setTasks((ts) => {
        const next = [task, ...ts]
        // Keep the newest few; a running task is never dropped.
        const keep: Task[] = []
        for (const t of next) if (keep.length < KEEP || t.state === 'running') keep.push(t)
        return keep
      })
      fly(spec.file || spec.label)
      say(makesFile ? TOAST.building : TOAST.running)
      const progress = (pct: number) => patch(id, { pct: Math.max(0, Math.min(100, Math.round(pct))) })
      Promise.resolve()
        .then(() => spec.run(progress))
        .then((o) => {
          const out = o || {}
          patch(id, { state: 'done', pct: 100, file: out.file || null, note: out.note || spec.note, problem: out.problem || null, seen: false })
          say(out.problem ? TOAST.problem(spec.label) : out.file ? TOAST.ready(spec.label) : TOAST.finished(spec.label))
          spec.onDone?.(out)
        })
        .catch((e) => {
          patch(id, { state: 'failed', problem: errorText(e), seen: false })
          say(TOAST.failed(spec.label))
        })
    },
    [fly, say]
  )

  const toggle = useCallback(() => {
    setOpen((o) => {
      // Opening is the acknowledgement: every task is now seen.
      if (!o) setTasks((ts) => ts.map((t) => (t.seen ? t : { ...t, seen: true })))
      return !o
    })
  }, [])
  const close = useCallback(() => setOpen(false), [])

  const value = { startTask, state: { tasks, open, landing, toast, toggle, close } }
  return (
    <Ctx.Provider value={value}>
      {children}
      {toast &&
        createPortal(
          <div className={styles.toast} role="status" aria-live="polite" data-testid="task-toast">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a7c07a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {toast}
          </div>,
          document.body
        )}
    </Ctx.Provider>
  )
}

const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`

const STATE_LABEL = (t: Task) => (t.state === 'running' ? 'Running' : t.state === 'failed' ? 'Failed' : t.file ? 'Ready' : 'Done')

// The rail item: bottom of the nav rail, on every page.
export function TaskCentreButton() {
  const c = useContext(Ctx)
  const btn = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; bottom: number } | null>(null)
  const open = !!c?.state.open
  const close = c?.state.close

  // A light popover, not a dialog: a press anywhere off it closes it, and the
  // press still reaches whatever it landed on. It is a layer (h 425-458), so
  // Escape closes the popover only, not a modal it is floating over.
  useLayer(open && !!close, () => close?.())
  useEffect(() => {
    if (!open || !close) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (pop.current?.contains(t) || btn.current?.contains(t)) return
      close()
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [open, close])

  useEffect(() => {
    if (!open || !btn.current) return
    const r = btn.current.getBoundingClientRect()
    setAt({ left: Math.max(8, r.left), bottom: Math.max(8, window.innerHeight - r.top + 8) })
  }, [open])

  if (!c) return null
  const { tasks, landing, toggle } = c.state
  const running = tasks.filter((t) => t.state === 'running')
  // Lifted above any modal until opened; the surface switches with it.
  const up = landing || open || tasks.some((t) => !t.seen)
  const label = running.length ? `${running.length} ${running.length === 1 ? 'task' : 'tasks'} running` : 'Background tasks'
  const sub = running.length ? running[0].label : `${tasks.length} recent`

  return (
    <div className={`${styles.wrap} ${up ? styles.wrapUp : ''}`} data-lifted={up ? 'true' : undefined}>
      <button
        ref={btn}
        type="button"
        data-task-btn
        className={[styles.btn, up ? styles.btnUp : '', landing ? styles.btnLand : ''].join(' ')}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={toggle}
      >
        {running.length > 0 && <span className={styles.spin} aria-hidden="true" />}
        <span className={styles.text}>
          <span className={running.length ? styles.labelRun : styles.label}>{label}</span>
          <span className={styles.sub}>{sub}</span>
        </span>
      </button>
      {open &&
        createPortal(
          <div ref={pop} className={styles.pop} style={at ? { left: at.left, bottom: at.bottom } : undefined} role="region" aria-label="Background tasks">
            <div className={styles.popHead}>Background tasks</div>
            {tasks.length === 0 && <div className={styles.empty}>Exports and recalculations you start appear here.</div>}
            {tasks.map((t) => (
              <div key={t.id} className={styles.item} data-testid="task-item">
                <div className={styles.itemHead}>
                  <span className={styles.itemLabel}>{t.label}</span>
                  <span className={`${styles.state} ${t.state === 'running' ? styles.stateRun : t.state === 'failed' ? styles.stateBad : ''}`}>{STATE_LABEL(t)}</span>
                </div>
                <span className={styles.note}>
                  {hhmm(t.startedAt)} · {t.note}
                </span>
                {t.state === 'running' && (
                  <div className={styles.bar} role="progressbar" aria-label={`${t.label} progress`} aria-valuenow={t.pct ?? undefined} aria-valuemin={0} aria-valuemax={100}>
                    {t.pct === null ? <span className={styles.barIndeterminate} /> : <span className={styles.barFill} style={{ width: t.pct + '%' }} />}
                  </div>
                )}
                {t.problem && <span className={t.state === 'failed' ? styles.problemBad : styles.problem}>{t.problem}</span>}
                {t.state === 'done' && t.file && (
                  <button type="button" className={styles.get} onClick={() => download(t.file!.name, t.file!.content, t.file!.type)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 3v12" />
                      <path d="m7 10 5 5 5-5" />
                      <path d="M5 21h14" />
                    </svg>
                    {t.file.name}
                  </button>
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  )
}
